import { useApp, useBehavior, useList, useTriggerAction, fillTemplate, parseTemplates } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, useMemo } from 'react';
import { TConsumerListData } from '../../types/data';
import { TListTemplateOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './styles/styles.module.css';

type TListTemplate = {
    Package: React.ElementType;
} & TWidgetProps<TConsumerListData, TListTemplateOption>;

export const ListTemplate: React.FC<TListTemplate> = ({ widget, Package }) => {
    const { setModal, setPopup, setBottomSheet } = useApp();
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { handleClick } = useList(widget, { triggerActionsByType, setModal, setPopup, setBottomSheet });

    const data = widget.data ?? {};
    const parsedTemplates = useMemo(() => parseTemplates(data.templates ?? []), [data.templates]);

    const filledItems = useMemo(
        () =>
            (Array.isArray(data.items) ? data.items : [])
                .filter((i) => i !== null && i !== undefined)
                .map((item, index) => ({ item, key: item.id ?? index, widgets: fillTemplate(item, parsedTemplates) })),
        [data.items, parsedTemplates],
    );

    if (parsedTemplates && Array.isArray(data.items) && data.items.length > 0) {
        const useHoverStateForClickableItems = widget.options?.useHoverStateForClickableItems ?? true;
        return (
            <Activity mode={isHidden ? 'hidden' : 'visible'}>
                <div
                    data-test={widget.uid}
                    aria-disabled={isDisabled}
                    className={clsx(isDisabled && styles.disabled)}
                    style={getInlineStyles({ margin: widget.options?.margin })}
                >
                    {filledItems.map(({ item, key, widgets }) => {
                        if (item.clickable === false) {
                            return <Package key={key} widgets={widgets} />;
                        } else {
                            return (
                                <div
                                    key={key}
                                    className={clsx(styles.templateListItem, useHoverStateForClickableItems && !isDisabled && styles.hoverState)}
                                    onClick={(_e) => {
                                        if (!isDisabled) {
                                            handleClick(widget.uid, item);
                                        }
                                    }}
                                >
                                    <Package widgets={widgets} />
                                </div>
                            );
                        }
                    })}
                </div>
            </Activity>
        );
    }
    return null;
};
